import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import {
  Plus, Briefcase, Users, Send, Edit2, Trash2,
  X, Search, MapPin, Calendar, Star,
  Download, Filter, Clock, CheckCircle, XCircle, Eye,
  ExternalLink, Phone, GraduationCap, User, Mail, Link,
  ChevronLeft, ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Job, EmploymentType, ExperienceLevel, JobStatus, SalaryRange, ApplicationStatus } from '../types'
import { SkillsPicker } from '../components/SkillsPicker'
import {
  EMPLOYMENT_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS, SALARY_LABELS,
} from '../constants/profileOptions'
import { notifyStatusEmail } from '../utils/emailNotify'

// ---- helpers ----
const statusColors: Record<JobStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  closed: 'bg-red-100 text-red-700'
}

const typeLabels = EMPLOYMENT_TYPE_LABELS

const levelLabels = EXPERIENCE_LEVEL_LABELS

const salaryLabels = SALARY_LABELS

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

// ---- form state ----
interface JobFormData {
  title: string
  description: string
  employment_type: EmploymentType
  experience_level: ExperienceLevel
  location: string
  salary_range: SalaryRange | ''
  skills_required: string[]
  requirements: string[]
  deadline: string
  is_partner_listing: boolean
  target_departments: string[]
}

const emptyForm: JobFormData = {
  title: '',
  description: '',
  employment_type: 'full_time',
  experience_level: 'entry',
  location: '',
  salary_range: '',
  skills_required: [],
  requirements: [],
  deadline: '',
  is_partner_listing: false,
  target_departments: []
}

// ---- applications ----
const APP_SALARY_LABELS: Record<string, string> = {
  under_20k: 'Under ₱20,000', '20k_40k': '₱20,000 – ₱40,000',
  '40k_60k': '₱40,000 – ₱60,000', '60k_80k': '₱60,000 – ₱80,000',
  '80k_100k': '₱80,000 – ₱100,000', over_100k: 'Over ₱100,000',
}
const AVAIL_LABELS: Record<string, string> = {
  immediately: 'Immediately available', two_weeks: '2 weeks notice',
  one_month: '1 month notice', three_months: '3+ months',
}

interface AppRow {
  id: string
  job_id: string
  job_title: string
  alumni_id: string
  alumni_name: string
  alumni_email: string
  alumni_program?: string
  alumni_department?: string
  alumni_year_graduated?: number
  alumni_skills?: string[]
  alumni_phone?: string
  cover_letter?: string
  resume_url?: string
  employer_notes?: string
  application_data?: Record<string, any>
  status: ApplicationStatus
  applied_at: string
}

const appStatusConfig: Record<ApplicationStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending:     { label: 'Applied',      color: 'bg-yellow-100 text-yellow-700',   icon: <Clock       className="w-3.5 h-3.5" /> },
  reviewed:    { label: 'Under Review', color: 'bg-blue-100 text-blue-700',       icon: <Eye         className="w-3.5 h-3.5" /> },
  shortlisted: { label: 'Interview',    color: 'bg-green-100 text-green-700',     icon: <Star        className="w-3.5 h-3.5" /> },
  rejected:    { label: 'Rejected',      color: 'bg-red-100 text-red-700',         icon: <XCircle     className="w-3.5 h-3.5" /> },
  hired:       { label: 'Hired',        color: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  cancelled:   { label: 'Withdrawn',    color: 'bg-gray-100 text-gray-500',       icon: <XCircle     className="w-3.5 h-3.5" /> },
}

const EmployerJobManagement: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  // data
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState('')
  const [isPartner, setIsPartner] = useState(false)
  const [allDepts, setAllDepts] = useState<string[]>([])

  // filters
  const [statusFilter, setStatusFilter] = useState<'all' | JobStatus>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [jobPage, setJobPage] = useState(1)

  // tabs — sync with route so sidebar navigation works
  const [activeTab, setActiveTab] = useState<'listings' | 'applications'>(
    location.pathname === '/employer/applications' ? 'applications' : 'listings'
  )

  useEffect(() => {
    setActiveTab(location.pathname === '/employer/applications' ? 'applications' : 'listings')
  }, [location.pathname])

  // applications tab state
  const [applications, setApplications] = useState<AppRow[]>([])
  const [appsLoading, setAppsLoading] = useState(false)
  const [appStatusFilter, setAppStatusFilter] = useState<'all' | ApplicationStatus>('all')
  const [jobFilter, setJobFilter] = useState<'all' | string>('all')
  const [appSearch, setAppSearch] = useState('')
  const [viewModalApp, setViewModalApp] = useState<AppRow | null>(null)
  const [modalNotes, setModalNotes] = useState('')
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [confirmStatus, setConfirmStatus] = useState<{ appId: string; newStatus: ApplicationStatus } | null>(null)
  const [profileModalApp, setProfileModalApp] = useState<AppRow | null>(null)
  const [profileData, setProfileData] = useState<Record<string, any> | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [postHireDialog, setPostHireDialog] = useState<{
    jobId: string
    hiredAppId: string
    remainingCount: number
    closeJob: boolean
    rejectOthers: boolean
  } | null>(null)

  // modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [showPublishConfirm, setShowPublishConfirm] = useState(false)
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [viewingJob, setViewingJob] = useState<Job | null>(null)
  const [formData, setFormData] = useState<JobFormData>(emptyForm)
  const [saving, setSaving] = useState(false)

  // tag inputs
  const [reqInput, setReqInput] = useState('')

  // ---- fetch employer data ----
  const fetchEmployerData = useCallback(async () => {
    if (!user) return
    try {
      const { data: ep } = await supabaseClient
        .from('employer_profiles')
        .select('company_id, is_industry_partner, companies(id, name)')
        .eq('user_id', user.id)
        .single()

      if (ep) {
        setCompanyId(ep.company_id)
        setCompanyName((ep.companies as any)?.name || '')
        setIsPartner(ep.is_industry_partner || false)

        // Fetch all departments for target_departments selector
        const { data: depts } = await supabaseClient
          .from('departments')
          .select('name')
          .order('name')
        setAllDepts((depts || []).map((d: any) => d.name).filter(Boolean))
      }
    } catch (err) {
      console.error('Error fetching employer data:', err)
    }
  }, [user])

  // ---- fetch jobs ----
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true)
      // Close any jobs whose deadline has passed before fetching
      await supabaseClient.rpc('close_expired_jobs')
      const { data, error } = await supabaseClient.rpc('get_employer_jobs')
      if (error) throw error
      setJobs((data || []) as Job[])
    } catch (err: any) {
      console.error('Error fetching jobs:', err)
      toast.error('Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEmployerData().then(fetchJobs)
  }, [fetchEmployerData, fetchJobs])

  // ---- fetch all applications (direct query, no RPC) ----
  const fetchApplications = useCallback(async () => {
    if (!user?.id) return
    setAppsLoading(true)
    try {
      const { data: jobsData } = await supabaseClient
        .from('jobs')
        .select('id, title')
        .eq('posted_by', user.id)
        .is('deleted_at', null)
        .order('posted_at', { ascending: false })
      const allJobs = (jobsData || []) as { id: string; title: string }[]

      if (allJobs.length === 0) { setApplications([]); return }

      const jobIds = allJobs.map(j => j.id)
      const { data: appData, error: appErr } = await supabaseClient
        .from('job_applications')
        .select('id, job_id, alumni_id, status, cover_letter, resume_url, applied_at, employer_notes, application_data')
        .in('job_id', jobIds)
        .neq('status', 'cancelled')
        .order('applied_at', { ascending: false })
      if (appErr) throw appErr

      const rawApps = appData || []
      if (rawApps.length === 0) { setApplications([]); return }

      const alumniIds = [...new Set(rawApps.map((a: any) => a.alumni_id))]
      const [{ data: profiles }, { data: users }] = await Promise.all([
        supabaseClient.from('alumni_profiles')
          .select('user_id, first_name, last_name, program, department, year_graduated, skills_acquired, phone')
          .in('user_id', alumniIds),
        supabaseClient.from('users')
          .select('id, email')
          .in('id', alumniIds)
      ])

      const profileMap = Object.fromEntries((profiles || []).map((p: any) => [p.user_id, p]))
      const emailMap = Object.fromEntries((users || []).map((u: any) => [u.id, u.email]))
      const jobTitleMap = Object.fromEntries(allJobs.map(j => [j.id, j.title]))

      const rows: AppRow[] = rawApps.map((a: any) => {
        const p = profileMap[a.alumni_id] || {}
        return {
          id: a.id,
          job_id: a.job_id,
          job_title: jobTitleMap[a.job_id] || 'Unknown Job',
          alumni_id: a.alumni_id,
          alumni_name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown',
          alumni_email: emailMap[a.alumni_id] || '',
          alumni_program: p.program,
          alumni_department: p.department,
          alumni_year_graduated: p.year_graduated,
          alumni_skills: p.skills_acquired || [],
          alumni_phone: p.phone,
          cover_letter: a.cover_letter,
          resume_url: a.resume_url,
          employer_notes: a.employer_notes,
          application_data: a.application_data || {},
          status: a.status,
          applied_at: a.applied_at
        }
      })
      setApplications(rows)
    } catch (err: any) {
      console.error('Failed to load applications:', err)
      toast.error('Failed to load applications')
    } finally {
      setAppsLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (activeTab === 'applications') fetchApplications()
  }, [activeTab, fetchApplications])

  useEffect(() => { setJobPage(1) }, [statusFilter, searchTerm])

  const updateAppStatus = async (appId: string, newStatus: ApplicationStatus) => {
    try {
      setUpdatingId(appId)
      const { error } = await supabaseClient.rpc('update_application_status', {
        p_app_id: appId,
        p_status: newStatus,
      })
      if (error) throw error
      toast.success(`Marked as ${newStatus}`)
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: newStatus } : a))
      // Notify alumni by email on terminal status changes
      if (newStatus === 'rejected') {
        notifyStatusEmail(appId, 'rejected')
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status')
    } finally {
      setUpdatingId(null)
    }
  }

  const handleHireApplication = async (appId: string, app: AppRow) => {
    try {
      setUpdatingId(appId)
      const { error } = await supabaseClient.rpc('update_application_status', {
        p_app_id: appId,
        p_status: 'hired',
      })
      if (error) throw error
      toast.success('Marked as hired')
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, status: 'hired' } : a))
      setViewModalApp({ ...app, status: 'hired' })
      setConfirmStatus(null)
      // Notify alumni by email
      notifyStatusEmail(appId, 'hired')
      const remaining = applications.filter(a =>
        a.job_id === app.job_id &&
        a.id !== appId &&
        ['pending', 'reviewed', 'shortlisted'].includes(a.status)
      ).length
      setPostHireDialog({
        jobId: app.job_id,
        hiredAppId: appId,
        remainingCount: remaining,
        closeJob: true,
        rejectOthers: false,
      })
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status')
    } finally {
      setUpdatingId(null)
    }
  }

  const handlePostHireApply = async () => {
    if (!postHireDialog) return
    const { jobId, hiredAppId, closeJob, rejectOthers, remainingCount } = postHireDialog
    try {
      if (closeJob) {
        const { error } = await supabaseClient
          .from('jobs')
          .update({ status: 'closed', closed_by: user?.id, close_reason: 'Position filled' })
          .eq('id', jobId)
        if (error) throw error
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'closed' as const } : j))
        toast.success('Job listing closed')
      }
      if (rejectOthers && remainingCount > 0) {
        const toRejectIds = applications
          .filter(a => a.job_id === jobId && a.id !== hiredAppId && ['pending', 'reviewed', 'shortlisted'].includes(a.status))
          .map(a => a.id)
        const { error } = await supabaseClient
          .from('job_applications')
          .update({ status: 'rejected', reviewed_at: new Date().toISOString() })
          .in('status', ['pending', 'reviewed', 'shortlisted'])
          .eq('job_id', jobId)
          .neq('id', hiredAppId)
        if (error) throw error
        toRejectIds.forEach(id => notifyStatusEmail(id, 'rejected').catch(() => {}))
        setApplications(prev => prev.map(a =>
          a.job_id === jobId && a.id !== hiredAppId && ['pending', 'reviewed', 'shortlisted'].includes(a.status)
            ? { ...a, status: 'rejected' as ApplicationStatus }
            : a
        ))
        toast.success(`${remainingCount} applicant${remainingCount !== 1 ? 's' : ''} rejected`)
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to apply post-hire actions')
    }
    setPostHireDialog(null)
  }

  const updateAppNotes = async (appId: string, notes: string, prev: string | undefined) => {
    if (notes === (prev || '')) return
    try {
      const { error } = await supabaseClient
        .from('job_applications').update({ employer_notes: notes }).eq('id', appId)
      if (error) throw error
      toast.success('Notes saved')
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, employer_notes: notes } : a))
    } catch (err: any) {
      toast.error('Failed to save notes')
    }
  }

  const openViewModal = (app: AppRow) => {
    setViewModalApp(app)
    setModalNotes(app.employer_notes || '')
  }

  const closeViewModal = () => {
    if (viewModalApp && modalNotes !== (viewModalApp.employer_notes || '')) {
      updateAppNotes(viewModalApp.id, modalNotes, viewModalApp.employer_notes)
    }
    setConfirmStatus(null)
    setViewModalApp(null)
  }

  const handleDownloadResume = async (resumePath: string) => {
    try {
      const { data, error } = await supabaseClient.storage
        .from('resumes')
        .createSignedUrl(resumePath, 300)
      if (error) throw error
      window.open(data.signedUrl, '_blank')
    } catch {
      toast.error('Failed to get resume download link')
    }
  }

  const openProfileModal = async (app: AppRow) => {
    setProfileModalApp(app)
    setProfileLoading(true)
    try {
      const { data } = await supabaseClient
        .from('alumni_profiles')
        .select('first_name, last_name, program, department, year_graduated, skills_acquired, profile_picture, phone, current_employment_status, field_of_work, job_position, employment_length')
        .eq('user_id', app.alumni_id)
        .single()
      setProfileData(data || {})
    } catch {
      setProfileData(null)
    } finally {
      setProfileLoading(false)
    }
  }

  // ---- filtered jobs ----
  const filteredJobs = jobs.filter(j => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false
    if (searchTerm) {
      const s = searchTerm.toLowerCase()
      return j.title.toLowerCase().includes(s) || (j.location || '').toLowerCase().includes(s)
    }
    return true
  })

  const JOB_PAGE_SIZE = 9
  const jobTotalPages = Math.max(1, Math.ceil(filteredJobs.length / JOB_PAGE_SIZE))
  const paginatedJobs = filteredJobs.slice((jobPage - 1) * JOB_PAGE_SIZE, jobPage * JOB_PAGE_SIZE)

  // stats
  const stats = {
    total: jobs.length,
    active: jobs.filter(j => j.status === 'active').length,
    drafts: jobs.filter(j => j.status === 'draft').length,
    applications: jobs.reduce((sum, j) => sum + (j.application_count || 0), 0)
  }

  // ---- shared validation ----
  const validateForm = (): string | null => {
    const title = formData.title.trim()
    const desc = formData.description.trim()
    if (!title) return 'Job title is required'
    if (title.length < 3) return 'Job title must be at least 3 characters'
    if (title.length > 150) return 'Job title must be under 150 characters'
    if (!desc) return 'Description is required'
    if (desc.length < 20) return 'Description must be at least 20 characters'
    if (formData.deadline) {
      const dl = new Date(formData.deadline)
      if (isNaN(dl.getTime())) return 'Invalid deadline date'
      if (dl < new Date()) return 'Deadline cannot be in the past'
    }
    if (formData.target_departments.length === 0) return 'Please select at least one target department'
    return null
  }

  // ---- create job ----
  const handleCreate = async () => {
    const validationError = validateForm()
    if (validationError) {
      toast.error(validationError)
      return
    }
    if (!companyId) {
      toast.error('Company profile not loaded yet. Please wait and try again.')
      return
    }
    try {
      setSaving(true)
      const { error } = await supabaseClient.from('jobs').insert({
        title: formData.title.trim(),
        description: formData.description.trim(),
        company_id: companyId,
        company_name: companyName,
        employment_type: formData.employment_type,
        experience_level: formData.experience_level,
        location: formData.location.trim() || null,
        salary_range: formData.salary_range || null,
        skills_required: formData.skills_required,
        requirements: formData.requirements,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        is_partner_listing: formData.is_partner_listing,
        target_departments: formData.target_departments,
        posted_by: user?.id,
        status: 'draft'
      })
      if (error) throw error
      toast.success('Job posting created as draft')
      setShowCreateModal(false)
      setFormData(emptyForm)
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to create job')
    } finally {
      setSaving(false)
    }
  }

  // ---- edit job ----
  const openEdit = (job: Job) => {
    setSelectedJob(job)
    setFormData({
      title: job.title,
      description: job.description || '',
      employment_type: job.employment_type,
      experience_level: job.experience_level,
      location: job.location || '',
      salary_range: (job.salary_range as SalaryRange) || '',
      skills_required: job.skills_required || [],
      requirements: job.requirements || [],
      deadline: job.deadline ? new Date(job.deadline).toISOString().slice(0, 16) : '',
      is_partner_listing: job.is_partner_listing || false,
      target_departments: job.target_departments || []
    })
    setShowEditModal(true)
  }

  const handleEdit = async () => {
    if (!selectedJob) return
    const validationError = validateForm()
    if (validationError) {
      toast.error(validationError)
      return
    }
    try {
      setSaving(true)
      const isActive = selectedJob.status === 'active'
      const updatePayload: Record<string, any> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        skills_required: formData.skills_required,
        requirements: formData.requirements,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        is_partner_listing: formData.is_partner_listing,
        target_departments: formData.target_departments
      }
      // Material terms are locked for active jobs to protect existing applicants
      if (!isActive) {
        updatePayload.employment_type = formData.employment_type
        updatePayload.experience_level = formData.experience_level
        updatePayload.location = formData.location.trim() || null
        updatePayload.salary_range = formData.salary_range || null
      }
      const { error } = await supabaseClient.from('jobs').update(updatePayload).eq('id', selectedJob.id)
      if (error) throw error
      toast.success('Job updated')
      setShowEditModal(false)
      setSelectedJob(null)
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update job')
    } finally {
      setSaving(false)
    }
  }

  // ---- publish draft → active ----
  const handlePublish = async () => {
    if (!selectedJob) return
    try {
      const { error } = await supabaseClient.from('jobs').update({
        status: 'active',
        posted_at: new Date().toISOString()
      }).eq('id', selectedJob.id)
      if (error) throw error
      toast.success('Job published!')
      setShowPublishConfirm(false)
      setSelectedJob(null)
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish job')
    }
  }

  // ---- close job ----
  const handleClose = async () => {
    if (!selectedJob) return
    try {
      const { error } = await supabaseClient.from('jobs').update({ status: 'closed' }).eq('id', selectedJob.id)
      if (error) throw error

      setJobs(prev => prev.map(j => j.id === selectedJob.id ? { ...j, status: 'closed' as const } : j))
      toast.success('Job closed')
      setShowCloseConfirm(false)
      setSelectedJob(null)
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to close job')
    }
  }

  // ---- delete draft ----
  const handleDelete = async () => {
    if (!selectedJob) return
    try {
      const { error } = await supabaseClient.from('jobs').update({
        deleted_at: new Date().toISOString()
      }).eq('id', selectedJob.id)
      if (error) throw error
      toast.success('Draft deleted')
      setShowDeleteConfirm(false)
      setSelectedJob(null)
      fetchJobs()
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete draft')
    }
  }

  // ---- tag helpers ----
  const addReq = () => {
    const r = reqInput.trim()
    if (r && !formData.requirements.includes(r)) {
      setFormData({ ...formData, requirements: [...formData.requirements, r] })
    }
    setReqInput('')
  }
  const removeReq = (req: string) => {
    setFormData({ ...formData, requirements: formData.requirements.filter(r => r !== req) })
  }

  // ---- job form JSX (shared by create & edit modals) ----
  const renderJobForm = (locked = false) => (
    <div className="space-y-4 max-h-[calc(100vh-16rem)] overflow-y-auto px-1">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title <span className="text-red-500">*</span></label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData({ ...formData, title: e.target.value })}
          maxLength={150}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g. Software Engineer"
        />
        <p className="text-xs text-gray-400 mt-1">{formData.title.length}/150</p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
        <textarea
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          rows={5}
          maxLength={5000}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Describe the role, responsibilities, and what you're looking for..."
        />
        <p className="text-xs text-gray-400 mt-1">{formData.description.length}/5000 (min 20)</p>
      </div>

      {/* Type + Level row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employment Type <span className="text-red-500">*</span>
            {locked && <span className="ml-1 text-xs text-gray-400">(locked)</span>}
          </label>
          <select
            value={formData.employment_type}
            onChange={e => setFormData({ ...formData, employment_type: e.target.value as EmploymentType })}
            disabled={locked}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {Object.entries(typeLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Experience Level <span className="text-red-500">*</span>
            {locked && <span className="ml-1 text-xs text-gray-400">(locked)</span>}
          </label>
          <select
            value={formData.experience_level}
            onChange={e => setFormData({ ...formData, experience_level: e.target.value as ExperienceLevel })}
            disabled={locked}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {Object.entries(levelLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location
          {locked && <span className="ml-1 text-xs text-gray-400">(locked)</span>}
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={e => setFormData({ ...formData, location: e.target.value })}
          disabled={locked}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          placeholder="e.g. Cebu City"
        />
      </div>

      {/* Salary Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Salary Range
          {locked && <span className="ml-1 text-xs text-gray-400">(locked)</span>}
        </label>
        <select
          value={formData.salary_range}
          onChange={e => setFormData({ ...formData, salary_range: e.target.value as SalaryRange | '' })}
          disabled={locked}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          <option value="">Prefer not to disclose</option>
          {Object.entries(salaryLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Skills */}
      <div>
        <SkillsPicker
          label="Required Skills"
          helperText="Technical or soft skills needed for this role (e.g. React, Project Management, AutoCAD)"
          value={formData.skills_required}
          onChange={(skills) => setFormData({ ...formData, skills_required: skills })}
          maxSkills={20}
          maxSkillLength={60}
          placeholder="Search skills or type a custom one…"
        />
      </div>

      {/* Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
        <p className="text-xs text-gray-500 mb-2">Qualifications or certifications needed (e.g. Bachelor's in CS, 2+ years experience, PRC License)</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={reqInput}
            onChange={e => setReqInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addReq() } }}
            maxLength={100}
            disabled={formData.requirements.length >= 15}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder={formData.requirements.length >= 15 ? 'Maximum 15 requirements reached' : 'Type a requirement and press Enter'}
          />
          <button type="button" onClick={addReq} disabled={formData.requirements.length >= 15} className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">Add</button>
        </div>
        {formData.requirements.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.requirements.map(r => (
              <span key={r} className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                {r}
                <button type="button" onClick={() => removeReq(r)} className="ml-1.5 text-gray-400 hover:text-gray-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Deadline */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
        <input
          type="datetime-local"
          value={formData.deadline}
          onChange={e => setFormData({ ...formData, deadline: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-500 mt-1">Leave empty for no deadline</p>
      </div>

      {/* Partner listing toggle — only for industry partners */}
      {isPartner && (
        <div className="border-t pt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_partner_listing}
              onChange={e => setFormData({ ...formData, is_partner_listing: e.target.checked })}
              className="w-4 h-4 text-yellow-600 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500" /> Partner Listing
              </span>
              <p className="text-xs text-gray-500">Boosted visibility for alumni from your partnered departments</p>
            </div>
          </label>
        </div>
      )}

      {/* Target departments — available for all employers */}
      {allDepts.length > 0 && (
        <div className={isPartner ? 'ml-0' : 'border-t pt-4'}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Departments <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">Select the departments whose alumni are best suited for this role. This doesn't restrict visibility — all alumni can still see the job.</p>
          <div className="grid grid-cols-2 gap-2">
            {allDepts.map(dept => (
              <label key={dept} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.target_departments.includes(dept)}
                  onChange={e => {
                    if (e.target.checked) {
                      setFormData({ ...formData, target_departments: [...formData.target_departments, dept] })
                    } else {
                      setFormData({ ...formData, target_departments: formData.target_departments.filter(d => d !== dept) })
                    }
                  }}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">{dept}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  // ── Applications tab filtered data ──
  const filteredApps = applications.filter(app => {
    if (appStatusFilter !== 'all' && app.status !== appStatusFilter) return false
    if (jobFilter !== 'all' && app.job_id !== jobFilter) return false
    if (appSearch) {
      const s = appSearch.toLowerCase()
      return (
        app.alumni_name.toLowerCase().includes(s) ||
        app.alumni_email.toLowerCase().includes(s) ||
        (app.alumni_program || '').toLowerCase().includes(s)
      )
    }
    return true
  })
  const appCounts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    hired: applications.filter(a => a.status === 'hired').length
  }
  // unique jobs list for filter dropdown
  const appJobList = [...new Map(applications.map(a => [a.job_id, { id: a.job_id, title: a.job_title }])).values()]

  // ---- MAIN RENDER ----
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Job Management</h1>
            <p className="text-blue-200 text-sm mt-1">Create and manage your job postings</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-yellow-300" />
                {stats.active} active
              </span>
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                <Edit2 className="w-3.5 h-3.5 text-yellow-300" />
                {stats.drafts} drafts
              </span>
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-yellow-300" />
                {stats.applications} applications
              </span>
            </div>
          </div>
          <button
            onClick={() => { setFormData(emptyForm); setShowCreateModal(true) }}
            className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-yellow-400 text-gray-900 rounded-xl hover:bg-yellow-300 transition font-bold shadow-lg text-sm"
          >
            <Plus className="w-5 h-5" />
            New Job
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-blue-900 px-4 py-2.5 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-white/80" />
            <span className="text-xs font-medium text-white/90">Total Jobs</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-400 mt-0.5">All job postings</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-yellow-400 px-4 py-2.5 flex items-center gap-2">
            <Send className="w-4 h-4 text-gray-800" />
            <span className="text-xs font-medium text-gray-800">Active</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
            <p className="text-xs text-gray-400 mt-0.5">Currently live</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-blue-900 px-4 py-2.5 flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-white/80" />
            <span className="text-xs font-medium text-white/90">Drafts</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{stats.drafts}</p>
            <p className="text-xs text-gray-400 mt-0.5">Pending publish</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-yellow-400 px-4 py-2.5 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-800" />
            <span className="text-xs font-medium text-gray-800">Applications</span>
          </div>
          <div className="px-4 py-3">
            <p className="text-3xl font-bold text-gray-900">{stats.applications}</p>
            <p className="text-xs text-gray-400 mt-0.5">Total received</p>
          </div>
        </div>
      </div>

      {/* ── Tab Switcher ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => { setActiveTab('listings'); navigate('/employer/jobs') }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'listings' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Briefcase className="w-4 h-4" /> All Job Listings
        </button>
        <button
          onClick={() => { setActiveTab('applications'); navigate('/employer/applications'); fetchApplications() }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === 'applications' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Users className="w-4 h-4" /> All Applications {applications.length > 0 && <span className="bg-blue-900 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{applications.length}</span>}
        </button>
        <button
          onClick={() => navigate('/employer/invitations')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition text-gray-600 hover:text-gray-900"
        >
          <Send className="w-4 h-4" /> Invitations Sent
        </button>
      </div>

      {activeTab === 'applications' ? (
        /* ══════════════ APPLICATIONS TAB ══════════════ */
        <div className="space-y-4">

          {/* Active job filter — full job detail card */}
          {jobFilter !== 'all' && (() => {
            const fj = jobs.find(j => j.id === jobFilter)
            if (!fj) return null
            return (
              <div className="bg-white border border-blue-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Header strip */}
                <div className="bg-blue-900 px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-white text-sm font-medium">
                    <Briefcase className="w-4 h-4 text-blue-300" />
                    Viewing applications for this job
                  </div>
                  <button
                    onClick={() => setJobFilter('all')}
                    className="flex items-center gap-1 text-xs font-medium text-blue-200 hover:text-white transition"
                  >
                    <X className="w-3.5 h-3.5" /> Show all jobs
                  </button>
                </div>
                {/* Job info body */}
                <div className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{fj.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mt-1">
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">{typeLabels[fj.employment_type] || fj.employment_type}</span>
                        <span className="text-gray-500">{levelLabels[fj.experience_level] || fj.experience_level}</span>
                        {fj.location && (
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" />{fj.location}</span>
                        )}
                        {fj.salary_range && (
                          <span className="text-green-700 font-medium">{salaryLabels[fj.salary_range] || fj.salary_range}</span>
                        )}
                        {fj.deadline && (
                          <span className={`flex items-center gap-1 font-medium ${new Date(fj.deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(fj.deadline) < new Date() ? 'Expired' : 'Deadline'}:{' '}
                            {formatDate(fj.deadline)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[fj.status]}`}>{fj.status}</span>
                  </div>

                  {fj.description && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Description</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line line-clamp-4">{fj.description}</p>
                    </div>
                  )}

                  {fj.requirements && fj.requirements.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        {fj.requirements.map((r, i) => (
                          <li key={i} className="text-sm text-gray-700">{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {fj.skills_required && fj.skills_required.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                      <div className="flex flex-wrap gap-1.5">
                        {fj.skills_required.map((s, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* search + job filter */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={appSearch}
                  onChange={e => setAppSearch(e.target.value)}
                  placeholder="Search by name, email, or program..."
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <select
                  value={jobFilter}
                  onChange={e => setJobFilter(e.target.value)}
                  className="pl-9 pr-8 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="all">All Jobs</option>
                  {appJobList.map(j => (
                    <option key={j.id} value={j.id}>{j.title}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* status filter */}
          <div className="flex flex-wrap gap-2">
            {(['all', 'pending', 'reviewed', 'shortlisted', 'hired', 'rejected'] as const).map(s => {
              const count = appCounts[s as keyof typeof appCounts]
              const isActive = appStatusFilter === s
              const badgeColors: Record<string, string> = {
                all: isActive ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-600',
                pending: isActive ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-700',
                reviewed: isActive ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700',
                shortlisted: isActive ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700',
                hired: isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700',
                rejected: isActive ? 'bg-red-500 text-white' : 'bg-red-100 text-red-600',
              }
              return (
                <button
                  key={s}
                  onClick={() => setAppStatusFilter(s)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition ${
                    isActive
                      ? 'bg-white border-gray-900 text-gray-900 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                  }`}
                >
                  {s === 'all' ? 'All' : appStatusConfig[s].label}
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center ${badgeColors[s]}`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          {/* loading / empty / cards */}
          {appsLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700">No applications found</h3>
              <p className="text-gray-500 mt-1 text-sm">
                {applications.length === 0
                  ? 'Applications will appear here when alumni apply to your jobs.'
                  : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredApps.map(app => {
                const sc = appStatusConfig[app.status]
                return (
                  <div
                    key={app.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition cursor-pointer"
                    onClick={() => openViewModal(app)}
                  >
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                          {app.alumni_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">{app.alumni_name || 'Unknown'}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                            {app.alumni_program && <span>{app.alumni_program}</span>}
                            {app.alumni_year_graduated && <span>Class of {app.alumni_year_graduated}</span>}
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              <span className="font-medium text-blue-700">{app.job_title}</span>
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> Applied: {formatDate(app.applied_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                          {sc.icon} {sc.label}
                        </span>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        /* ══════════════ LISTINGS TAB ══════════════ */
        <>
      {/* ── Search + Filter Bar ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search jobs by title or location..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(['all', 'active', 'draft', 'closed'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                  statusFilter === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Results count ── */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          <span className="text-2xl font-bold text-gray-900">{filteredJobs.length}</span>
          {' '}Job{filteredJobs.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ── Jobs Grid ── */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-2">
            {jobs.length === 0 ? 'No job postings yet' : 'No matching jobs'}
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            {jobs.length === 0
              ? 'Create your first job posting to start recruiting UCLM alumni.'
              : 'Try adjusting your search or filters.'}
          </p>
          {jobs.length === 0 && (
            <button
              onClick={() => { setFormData(emptyForm); setShowCreateModal(true) }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition font-bold"
            >
              <Plus className="w-5 h-5" /> Create Job Posting
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginatedJobs.map(job => {
            const iconColors = [
              'bg-blue-900 text-white', 'bg-yellow-400 text-gray-900', 'bg-indigo-600 text-white',
              'bg-emerald-600 text-white', 'bg-rose-600 text-white', 'bg-violet-600 text-white',
            ]
            const colorIdx = job.title.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % iconColors.length

            return (
              <div key={job.id} onClick={() => setViewingJob(job)} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col group cursor-pointer">
                <div className="p-5 flex-1 flex flex-col">
                  {/* Header row */}
                  <div className="flex items-start gap-3.5">
                    <div className={`w-11 h-11 rounded-xl ${iconColors[colorIdx]} flex items-center justify-center text-lg font-bold shrink-0`}>
                      {job.title.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 leading-snug group-hover:text-blue-900 transition-colors truncate">
                          {job.title}
                        </h3>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[job.status]}`}>
                          {job.status.toUpperCase()}
                        </span>
                        {job.status === 'closed' && job.closed_by && job.closed_by !== user?.id && (
                          <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                            CLOSED BY ADMIN
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{companyName}</p>
                    </div>
                    {job.is_partner_listing && (
                      <span className="shrink-0 bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                        <Star className="w-3 h-3" /> PARTNER
                      </span>
                    )}
                  </div>

                  {/* Location */}
                  {job.location && (
                    <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" /> {job.location}
                    </p>
                  )}

                  {/* Info row */}
                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 flex-wrap">
                    <span className="font-medium">{levelLabels[job.experience_level]}</span>
                    <span className="text-gray-300">·</span>
                    <span>{typeLabels[job.employment_type]}</span>
                    {job.salary_range && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="text-green-700 font-medium">{salaryLabels[job.salary_range as SalaryRange] || job.salary_range}</span>
                      </>
                    )}
                  </div>

                  {/* Description snippet */}
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2 leading-relaxed flex-1">
                    {job.description}
                  </p>

                  {/* Skills tags */}
                  {(job.skills_required?.length || 0) > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {job.skills_required.slice(0, 4).map(s => (
                        <span key={s} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                      ))}
                      {job.skills_required.length > 4 && (
                        <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">+{job.skills_required.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Card footer — date + actions */}
                <div className="px-5 py-3.5 border-t border-gray-100 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <span className="font-medium text-gray-500">Posted:</span> {formatDate(job.posted_at)}
                      </span>
                      {job.deadline && (
                        <span className={`text-xs flex items-center gap-1 ${new Date(job.deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                          <Calendar className="w-3 h-3" />
                          <span className="font-medium">{new Date(job.deadline) < new Date() ? 'Expired:' : 'Deadline:'}</span>
                          {formatDate(job.deadline)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {job.status === 'draft' && (
                        <button onClick={e => { e.stopPropagation(); setSelectedJob(job); setShowPublishConfirm(true) }} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition" title="Publish">
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {(job.status === 'draft' || job.status === 'active') && (
                        <button onClick={e => { e.stopPropagation(); openEdit(job) }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Edit">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {job.status === 'active' && (
                        <button onClick={e => { e.stopPropagation(); setSelectedJob(job); setShowCloseConfirm(true) }} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition" title="Close">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {job.status === 'draft' && (
                        <button onClick={e => { e.stopPropagation(); setSelectedJob(job); setShowDeleteConfirm(true) }} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      setJobFilter(job.id)
                      setAppStatusFilter('all')
                      setAppSearch('')
                      setActiveTab('applications')
                      navigate('/employer/applications')
                      fetchApplications()
                    }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-900 text-xs font-semibold rounded-lg hover:bg-blue-100 transition"
                  >
                    <Users className="w-3.5 h-3.5" />
                    View Applications ({job.application_count || 0})
                  </button>
                  {job.status === 'active' && (
                    <button
                      onClick={e => {
                        e.stopPropagation()
                        navigate(`/employer/candidates?jobId=${job.id}&ai=1`)
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-50 text-green-900 text-xs font-semibold rounded-lg hover:bg-green-100 transition"
                    >
                      <Search className="w-3.5 h-3.5" />
                      Find Candidates
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {filteredJobs.length > 0 && jobTotalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2 pb-1">
          <button
            onClick={() => setJobPage(p => Math.max(1, p - 1))}
            disabled={jobPage === 1}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed transition"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-sm text-gray-500">
            Page <span className="font-semibold text-gray-700">{jobPage}</span> of <span className="font-semibold text-gray-700">{jobTotalPages}</span>
          </span>
          <button
            onClick={() => setJobPage(p => Math.min(jobTotalPages, p + 1))}
            disabled={jobPage === jobTotalPages}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 disabled:cursor-not-allowed transition"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      </>
      )}

      {/* ==================== MODALS ==================== */}
      {createPortal(<>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">Create Job Posting</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {renderJobForm()}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium">Cancel</button>
              <button onClick={handleCreate} disabled={saving} className="px-6 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition text-sm font-bold disabled:opacity-50">
                {saving ? 'Creating...' : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">Edit Job Posting</h2>
              <button onClick={() => { setShowEditModal(false); setSelectedJob(null) }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            {selectedJob.status === 'active' && (
              <div className="mx-6 mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <span className="text-amber-500 text-sm mt-0.5">⚠️</span>
                <p className="text-sm text-amber-800">
                  <strong>Some fields are locked</strong> because this job is active. Employment type, experience level, location, and salary range cannot be changed to protect existing applications.
                </p>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-6">
              {renderJobForm(selectedJob.status === 'active')}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => { setShowEditModal(false); setSelectedJob(null) }} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium">Cancel</button>
              <button onClick={handleEdit} disabled={saving} className="px-6 py-2.5 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition text-sm font-bold disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Detail Modal */}
      {viewingJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewingJob(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[calc(100vh-4rem)] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-bold shrink-0">
                  {viewingJob.title.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{viewingJob.title}</h2>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColors[viewingJob.status]}`}>
                      {viewingJob.status.toUpperCase()}
                    </span>
                    {viewingJob.is_partner_listing && (
                      <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                        <Star className="w-3 h-3" /> PARTNER
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{companyName}</p>
                </div>
              </div>
              <button onClick={() => setViewingJob(null)} className="p-1 hover:bg-gray-100 rounded-lg shrink-0"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Key info pills */}
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                  <Briefcase className="w-3.5 h-3.5 text-gray-400" /> {typeLabels[viewingJob.employment_type]}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                  {levelLabels[viewingJob.experience_level]}
                </span>
                {viewingJob.location && (
                  <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" /> {viewingJob.location}
                  </span>
                )}
                {viewingJob.salary_range && (
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">
                    {salaryLabels[viewingJob.salary_range as SalaryRange] || viewingJob.salary_range}
                  </span>
                )}
                {viewingJob.deadline && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium ${new Date(viewingJob.deadline) < new Date() ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'}`}>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(viewingJob.deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(viewingJob.deadline)}
                  </span>
                )}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{viewingJob.description}</p>
              </div>

              {/* Requirements */}
              {(viewingJob.requirements?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Requirements</h3>
                  <ul className="space-y-1">
                    {viewingJob.requirements.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills */}
              {(viewingJob.skills_required?.length || 0) > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Skills Required</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {viewingJob.skills_required.map(s => (
                      <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{viewingJob.application_count || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Posted</p>
                  <p className="text-sm font-semibold text-gray-700 mt-1">{formatDate(viewingJob.posted_at)}</p>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setViewingJob(null)
                  setJobFilter(viewingJob.id)
                  setAppStatusFilter('all')
                  setAppSearch('')
                  setActiveTab('applications')
                  navigate('/employer/applications')
                  fetchApplications()
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-blue-50 text-blue-900 rounded-lg hover:bg-blue-100 text-sm font-semibold transition"
              >
                <Users className="w-4 h-4" /> View Applications ({viewingJob.application_count || 0})
              </button>
              {viewingJob.status === 'active' && (
                <button
                  onClick={() => {
                    setViewingJob(null)
                    navigate(`/employer/candidates?jobId=${viewingJob.id}&ai=1`)
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-green-50 text-green-900 rounded-lg hover:bg-green-100 text-sm font-semibold transition"
                >
                  <Search className="w-4 h-4" /> Find Candidates
                </button>
              )}
              <div className="flex gap-2">
                {(viewingJob.status === 'draft' || viewingJob.status === 'active') && (
                  <button
                    onClick={() => { setViewingJob(null); openEdit(viewingJob) }}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition"
                  >
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                )}
                {viewingJob.status === 'draft' && (
                  <button
                    onClick={() => { setViewingJob(null); setSelectedJob(viewingJob); setShowPublishConfirm(true) }}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold transition"
                  >
                    <Send className="w-4 h-4" /> Publish
                  </button>
                )}
                {viewingJob.status === 'active' && (
                  <button
                    onClick={() => { setViewingJob(null); setSelectedJob(viewingJob); setShowCloseConfirm(true) }}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-semibold transition"
                  >
                    <X className="w-4 h-4" /> Close Job
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirm */}
      {showCloseConfirm && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Close Job Posting?</h3>
            <p className="text-gray-600 text-sm mb-6">"{selectedJob.title}" will stop accepting new applications. Existing applications will remain for your review.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowCloseConfirm(false); setSelectedJob(null) }} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-medium">Cancel</button>
              <button onClick={handleClose} className="px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-bold">Close Job</button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirm */}
      {showPublishConfirm && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Publish Job Posting?</h3>
            <p className="text-gray-600 text-sm mb-3">"{selectedJob.title}" will become visible to alumni and start accepting applications.</p>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-5">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Once published, the job's salary, employment type, experience level, and location will be locked to protect applicants.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowPublishConfirm(false); setSelectedJob(null) }} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-medium">Cancel</button>
              <button onClick={handlePublish} className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-bold">Publish</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Draft?</h3>
            <p className="text-gray-600 text-sm mb-6">"{selectedJob.title}" will be permanently removed.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowDeleteConfirm(false); setSelectedJob(null) }} className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-medium">Cancel</button>
              <button onClick={handleDelete} className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-bold">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Application view modal */}
      {viewModalApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => closeViewModal()}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                  {viewModalApp.alumni_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{viewModalApp.alumni_name || 'Unknown'}</h3>
                  <p className="text-xs text-gray-500">{viewModalApp.job_title} · Applied {formatDate(viewModalApp.applied_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(() => { const sc = appStatusConfig[viewModalApp.status]; return (
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}>{sc.icon} {sc.label}</span>
                ) })()}
                <button onClick={() => closeViewModal()} className="p-1 rounded-lg hover:bg-gray-100 transition"><X className="w-5 h-5 text-gray-400" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Contact + Resume */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Contact</h4>
                  <div className="space-y-1.5">
                    {viewModalApp.alumni_email && <p className="text-sm text-gray-600">{viewModalApp.alumni_email}</p>}
                    {(viewModalApp.alumni_phone || viewModalApp.application_data?.phone) && (
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        {viewModalApp.alumni_phone || viewModalApp.application_data?.phone}
                      </p>
                    )}
                    {viewModalApp.alumni_program && <p className="text-sm text-gray-500">{viewModalApp.alumni_program}</p>}
                    {viewModalApp.alumni_department && <p className="text-sm text-gray-500">{viewModalApp.alumni_department}</p>}
                    {viewModalApp.alumni_year_graduated && <p className="text-sm text-gray-500">Class of {viewModalApp.alumni_year_graduated}</p>}
                  </div>
                  <button
                    onClick={() => openProfileModal(viewModalApp)}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition"
                  >
                    <User className="w-3.5 h-3.5" /> View Full Profile
                  </button>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Resume</h4>
                  {viewModalApp.resume_url ? (
                    <button
                      onClick={() => handleDownloadResume(viewModalApp.resume_url!)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                    >
                      <Download className="w-3.5 h-3.5" /> Download Resume
                    </button>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No resume attached</p>
                  )}
                </div>
              </div>

              {/* Cover Letter */}
              {viewModalApp.cover_letter && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Cover Letter</h4>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {viewModalApp.cover_letter}
                  </div>
                </div>
              )}

              {/* Application details */}
              {viewModalApp.application_data && Object.keys(viewModalApp.application_data).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Application Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm bg-gray-50 rounded-lg p-3">
                    {viewModalApp.application_data.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-500">Phone:</span>
                        <span className="font-medium text-gray-800">{viewModalApp.application_data.phone}</span>
                      </div>
                    )}
                    {viewModalApp.application_data.portfolio_url && (
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-500">Portfolio:</span>
                        <a href={viewModalApp.application_data.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                          View Portfolio
                        </a>
                      </div>
                    )}
                    {viewModalApp.application_data.expected_salary && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Expected Salary:</span>
                        <span className="font-medium text-gray-800">{APP_SALARY_LABELS[viewModalApp.application_data.expected_salary] || viewModalApp.application_data.expected_salary}</span>
                      </div>
                    )}
                    {viewModalApp.application_data.availability && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-500">Availability:</span>
                        <span className="font-medium text-gray-800">{AVAIL_LABELS[viewModalApp.application_data.availability] || viewModalApp.application_data.availability}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Skills */}
              {viewModalApp.alumni_skills && viewModalApp.alumni_skills.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {viewModalApp.alumni_skills.map((s: string) => (
                      <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-md">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Details */}
              {(() => {
                const fj = jobs.find(j => j.id === viewModalApp.job_id)
                if (!fj) return null
                return (
                  <div className="border border-gray-100 rounded-xl bg-gray-50 p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-800">Applied Position</h4>
                    <div>
                      <p className="font-bold text-gray-900">{fj.title}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">{typeLabels[fj.employment_type] || fj.employment_type}</span>
                        <span>{levelLabels[fj.experience_level] || fj.experience_level}</span>
                        {fj.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-gray-400" />{fj.location}</span>}
                        {fj.salary_range && <span className="text-green-700 font-medium">{salaryLabels[fj.salary_range] || fj.salary_range}</span>}
                        {fj.deadline && (
                          <span className={`flex items-center gap-0.5 font-medium ${new Date(fj.deadline) < new Date() ? 'text-red-500' : 'text-orange-500'}`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(fj.deadline) < new Date() ? 'Expired' : 'Deadline'}: {formatDate(fj.deadline)}
                          </span>
                        )}
                      </div>
                    </div>
                    {fj.description && (
                      <p className="text-sm text-gray-700 leading-relaxed line-clamp-3 whitespace-pre-line">{fj.description}</p>
                    )}
                    {fj.requirements && fj.requirements.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Requirements</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {fj.requirements.map((r, i) => <li key={i} className="text-sm text-gray-700">{r}</li>)}
                        </ul>
                      </div>
                    )}
                    {fj.skills_required && fj.skills_required.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Skills Required</p>
                        <div className="flex flex-wrap gap-1.5">
                          {fj.skills_required.map((s, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Notes */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                <textarea
                  value={modalNotes}
                  onChange={e => setModalNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Add private notes about this applicant..."
                />
              </div>
            </div>

            {/* Footer: status actions */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              {confirmStatus && confirmStatus.appId === viewModalApp.id ? (
                <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-gray-700 flex-1">
                    Mark as <span className={`font-semibold ${appStatusConfig[confirmStatus.newStatus].color}`}>{appStatusConfig[confirmStatus.newStatus].label}</span>? The applicant's status will be updated.
                  </p>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setConfirmStatus(null)}
                      className="px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-100 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (confirmStatus.newStatus === 'hired') {
                          handleHireApplication(confirmStatus.appId, viewModalApp)
                        } else {
                          updateAppStatus(confirmStatus.appId, confirmStatus.newStatus)
                          setViewModalApp({ ...viewModalApp, status: confirmStatus.newStatus })
                          setConfirmStatus(null)
                        }
                      }}
                      disabled={updatingId === viewModalApp.id}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition disabled:opacity-50 ${appStatusConfig[confirmStatus.newStatus].color}`}
                    >
                      {updatingId === viewModalApp.id ? 'Updating...' : 'Confirm'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  {(['hired', 'rejected'] as ApplicationStatus[]).includes(viewModalApp.status) ? (
                    <span className="text-sm text-gray-400 italic">Status is final and cannot be changed.</span>
                  ) : (
                    <>
                      <span className="text-sm text-gray-500 mr-1">Change status:</span>
                      {(['reviewed', 'shortlisted', 'hired', 'rejected'] as ApplicationStatus[])
                        .filter(s => s !== viewModalApp.status)
                        .map(s => {
                          const cfg = appStatusConfig[s]
                          return (
                            <button
                              key={s}
                              onClick={() => setConfirmStatus({ appId: viewModalApp.id, newStatus: s })}
                              disabled={updatingId === viewModalApp.id}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition hover:opacity-80 disabled:opacity-50 ${cfg.color}`}
                            >
                              {cfg.icon} {cfg.label}
                            </button>
                          )
                        })}
                    </>
                  )}
                  <button
                    onClick={() => {
                      closeViewModal()
                      setJobFilter(viewModalApp.job_id)
                      setActiveTab('applications')
                      navigate('/employer/applications')
                      fetchApplications()
                    }}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    View all for this job →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alumni profile modal */}
      {profileModalApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setProfileModalApp(null); setProfileData(null) }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Full Profile</h3>
              <button onClick={() => { setProfileModalApp(null); setProfileData(null) }} className="p-1 rounded-lg hover:bg-gray-100 transition"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {profileLoading ? (
              <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : !profileData ? (
              <div className="p-8 text-center text-gray-500">Profile not found</div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">

                {/* ── Avatar + identity ── */}
                <div className="flex items-center gap-4">
                  {profileData.profile_picture ? (
                    <img src={profileData.profile_picture} alt="" className="w-16 h-16 rounded-2xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-blue-900 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                      {(profileData.first_name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-gray-900 leading-snug">{profileData.first_name} {profileData.last_name}</p>
                    {profileData.program && <p className="text-sm text-gray-500 mt-0.5">{profileData.program}</p>}
                    {profileData.department && <p className="text-xs text-gray-400">{profileData.department}</p>}
                  </div>
                </div>

                {/* ── Contact ── */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Contact</p>
                  <div className="space-y-2">
                    {profileModalApp.alumni_email && (
                      <div className="flex items-center gap-2.5 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${profileModalApp.alumni_email}`} className="hover:text-blue-600 transition">{profileModalApp.alumni_email}</a>
                      </div>
                    )}
                    {(profileData.phone || profileModalApp.alumni_phone) && (
                      <div className="flex items-center gap-2.5 text-sm text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        {profileData.phone || profileModalApp.alumni_phone}
                      </div>
                    )}
                    {profileModalApp.application_data?.portfolio_url && (
                      <div className="flex items-center gap-2.5 text-sm">
                        <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={profileModalApp.application_data.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                          {profileModalApp.application_data.portfolio_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Education ── */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Education</p>
                  <div className="grid grid-cols-2 gap-2">
                    {profileData.program && (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">Program</p>
                        <p className="text-sm font-semibold text-gray-800">{profileData.program}</p>
                      </div>
                    )}
                    {profileData.year_graduated && (
                      <div className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">Graduated</p>
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                          <GraduationCap className="w-3.5 h-3.5 text-gray-400" /> Class of {profileData.year_graduated}
                        </p>
                      </div>
                    )}
                    {profileData.department && (
                      <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                        <p className="text-xs text-gray-400 mb-0.5">Department</p>
                        <p className="text-sm font-semibold text-gray-800">{profileData.department}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Career ── */}
                {(profileData.current_employment_status || profileData.job_position || profileData.field_of_work) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Career</p>
                    <div className="grid grid-cols-2 gap-2">
                      {profileData.current_employment_status && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Employment Status</p>
                          <p className="text-sm font-semibold text-gray-800 capitalize">{profileData.current_employment_status.replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {profileData.job_position && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Current Position</p>
                          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                            <Briefcase className="w-3.5 h-3.5 text-gray-400" /> {profileData.job_position}
                          </p>
                        </div>
                      )}
                      {profileData.field_of_work && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-400 mb-0.5">Field of Work</p>
                          <p className="text-sm font-semibold text-gray-800">{profileData.field_of_work}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Skills ── */}
                {((profileData.skills_acquired?.length ?? 0) > 0 || (profileModalApp.alumni_skills?.length ?? 0) > 0) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(profileData.skills_acquired?.length > 0 ? profileData.skills_acquired : profileModalApp.alumni_skills || []).map((s: string) => (
                        <span key={s} className="bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-md">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Resume ── */}
                {profileModalApp.resume_url && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Resume</p>
                    <button
                      onClick={() => handleDownloadResume(profileModalApp.resume_url!)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 text-sm font-semibold transition"
                    >
                      <Download className="w-4 h-4" /> Download Resume
                    </button>
                  </div>
                )}

              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <button
                onClick={() => { setProfileModalApp(null); setProfileData(null) }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Hire Actions Dialog */}
      {postHireDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Applicant Hired!</h3>
                <p className="text-sm text-gray-500">Would you like to take any additional actions?</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
                <input
                  type="checkbox"
                  checked={postHireDialog.closeJob}
                  onChange={e => setPostHireDialog({ ...postHireDialog, closeJob: e.target.checked })}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Close this job listing</p>
                  <p className="text-xs text-gray-500 mt-0.5">Position is filled — stop accepting new applications</p>
                </div>
              </label>
              {postHireDialog.remainingCount > 0 && (
                <label className="flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
                  <input
                    type="checkbox"
                    checked={postHireDialog.rejectOthers}
                    onChange={e => setPostHireDialog({ ...postHireDialog, rejectOthers: e.target.checked })}
                    className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Reject {postHireDialog.remainingCount} remaining applicant{postHireDialog.remainingCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Update status for pending, reviewed, and shortlisted applicants</p>
                  </div>
                </label>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPostHireDialog(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Skip
              </button>
              <button
                onClick={handlePostHireApply}
                className="flex-1 px-4 py-2.5 bg-blue-900 text-white rounded-xl text-sm font-bold hover:bg-blue-800 transition"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      </>, document.body)}
    </div>
  )
}

export default EmployerJobManagement
